import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { FormattedMessage } from 'react-intl';
import { BasicList, Loader } from '@mummssoftware/common-ui';
import { formatPhysicianName } from '@mummssoftware/utils/formatters';
import { ATTENDING_FORM } from '../../../constants';
import { CertificationButtons } from './Buttons';
import { InlinePrintLabel } from './PrintLabels';
import CertBodyGrid from './BodyGrid';
import { CertNarrativeField } from './Narrative';
import messages from '../CertMessages';
import { PatientCertContext } from '../../Patient/PatientCertContext';
import { CertContext } from '../../../Context';
import { getFontSize } from './utils';

const visitDetailWrapper = styled.div``;

/**
 * @typedef f2fDetails
 *  @prop {string} [date]
 *  @prop {string} [link]
 *  @prop {boolean} [required]
 *  @prop {string} [provider]
 */
const PhysStatementDoctorName = ({ certPhysName, contextDoctor }) => {
  let Comp = <InlinePrintLabel />;
  if (certPhysName.length) {
    Comp = certPhysName;
  } else if (contextDoctor) {
    // OP-33278: statement uses "First Last"; label is the "Last, First" pick-list form
    Comp = contextDoctor.fullName || contextDoctor.label;
  }
  return Comp;
};

const CertDescriptionStatement = ({
  phys,
  isProvider,
  f2fRequired,
  providerSalutation,
  providerFirst,
  providerLast,
  providerCred,
  f2fDate,
  f2fLink,
  smallWidget,
  f2fMissing,
  additionalnarrative,
  openVisitDetails,
  historicalView,
}) => {
  const { selectedDoctor } = useContext(CertContext);
  const f2fVerbiage = isProvider
    ? 'I'
    : /* eslint-disable indent */
      formatPhysicianName(
        providerSalutation,
        providerFirst,
        providerLast,
        providerCred,
      );
  /* eslint-enable */
  const confirm = smallWidget ? 'shortf2fConfirm' : 'f2fConfirm';

  return (
    <div>
      <CertBodyGrid historicalView={historicalView}>
        {!additionalnarrative ? null : (
          <FormattedMessage {...messages.siteConfig} />
        )}
      </CertBodyGrid>

      <CertBodyGrid
        typographyProps={{ component: 'div' }}
        historicalView={historicalView}
      >
        {!smallWidget && (
          <FormattedMessage
            {...messages.physStatement}
            values={{
              name: (
                <PhysStatementDoctorName
                  certPhysName={phys}
                  contextDoctor={selectedDoctor}
                />
              ),
            }}
          />
        )}
      </CertBodyGrid>
      {(!smallWidget && f2fRequired) ||
      (smallWidget && f2fRequired && !f2fMissing) ? (
        <CertBodyGrid smallWidget={smallWidget} historicalView={historicalView}>
          <visitDetailWrapper
            onClick={openVisitDetails}
            style={{
              textDecoration: 'underline',
            }}
          >
            <FormattedMessage
              {...messages[confirm]}
              values={{
                f2fVerbiage,
                f2fLink,
                f2fDate,
                f2fFindings: isProvider ? (
                  <FormattedMessage {...messages.isF2fProvider} />
                ) : (
                  <FormattedMessage {...messages.notF2fProvider} />
                ),
              }}
            />
          </visitDetailWrapper>
        </CertBodyGrid>
      ) : null}
    </div>
  );
};

const CertConfirmationStatement = ({
  CertButtons,
  Footer,
  isDoctor,
  showVerbal,
  smallWidget,
  historicalView,
}) => {
  const statementStyle = { marginRight: 'auto', maxWidth: '650px' };

  return (
    <CertBodyGrid
      typographyProps={{ component: 'div' }}
      smallWidget={smallWidget}
      historicalView={historicalView}
    >
      <Grid container alignItems="center" justifyContent="center" spacing={1}>
        {!smallWidget && (
          // With my signature I hereby ... footer not needed smallWidget
          <Grid
            item
            xs={12}
            // eslint-disable-next-line no-nested-ternary
            sm={CertButtons && isDoctor ? (showVerbal ? 8 : 10) : 12} // Sorry
            style={statementStyle}
          >
            <Typography
              style={{ fontSize: getFontSize(smallWidget, historicalView) }}
            >
              <FormattedMessage {...messages.certAttest} />
            </Typography>
          </Grid>
        )}
        {Footer}
        {CertButtons}
      </Grid>
    </CertBodyGrid>
  );
};

/**
 *! This should not live here,but there are fires to put out. This needs to be handled
 *! correctly, and in a way that is scalable.
 */
const attdKeys = {
  default: 'attd',
  northidaho: 'nid-attd',
};
let attdMsgKey = attdKeys.default;
let attendingMessageKeys;
const setAttendingKeys = () => {
  attendingMessageKeys = [];
  for (let i = 0; i < 8; i++) {
    const msgKey = `${attdMsgKey}.${i + 1}`;
    if (messages[msgKey]) {
      attendingMessageKeys.push(msgKey);
    }
  }
};
setAttendingKeys();

const QuestionTypography = styled(Typography)`
  margin-bottom: -1em;
`;

const CertBodyAttending = ({
  CertButtons,
  Footer,
  trailingMessage,
  smallWidget,
  additionalnarrative,
  historicalView,
}) => {
  const { palette } = useTheme();
  return (
    <PatientCertContext.Consumer>
      {({ hospiceBin }) => {
        const prevKey = attdMsgKey;
        if (attdKeys[hospiceBin] && attdMsgKey !== attdKeys[hospiceBin]) {
          attdMsgKey = attdKeys[hospiceBin];
        } else if (!attdKeys[hospiceBin] && attdMsgKey !== attdKeys.default) {
          attdMsgKey = attdKeys.default;
        }
        if (attdMsgKey !== prevKey) {
          setAttendingKeys();
        }
        return (
          <Fragment>
            {messages[`${attdMsgKey}.header`] ? (
              <CertBodyGrid
                typographyProps={{ component: 'div' }}
                smallWidget={smallWidget}
                historicalView={historicalView}
              >
                <FormattedMessage {...messages[`${attdMsgKey}.header`]} />
              </CertBodyGrid>
            ) : null}
            <CertBodyGrid
              typographyProps={{ component: 'div' }}
              smallWidget={smallWidget}
              historicalView={historicalView}
            >
              {!additionalnarrative ? null : (
                <FormattedMessage {...messages.siteConfig} />
              )}
              {messages[`${attdMsgKey}.question`] ? (
                <QuestionTypography>
                  <FormattedMessage {...messages[`${attdMsgKey}.question`]} />
                </QuestionTypography>
              ) : null}
              {!smallWidget && (
                <BasicList
                  type="unordered"
                  messages={messages}
                  messageKeys={attendingMessageKeys}
                  style={{ fontSize: getFontSize(smallWidget, historicalView) }}
                />
              )}
              {trailingMessage ? (
                <CertBodyGrid
                  typographyProps={{ color: 'textSecondary' }}
                  smallWidget={smallWidget}
                  historicalView={historicalView}
                >
                  {trailingMessage}
                </CertBodyGrid>
              ) : null}
            </CertBodyGrid>
            {messages[`${attdMsgKey}.footer`] ? (
              <CertBodyGrid
                typographyProps={{ component: 'div' }}
                smallWidget={smallWidget}
                historicalView={historicalView}
              >
                <FormattedMessage {...messages[`${attdMsgKey}.footer`]} />
              </CertBodyGrid>
            ) : null}
            {Footer}
            {CertButtons ? (
              <CertBodyGrid
                typographyProps={{ component: 'div' }}
                smallWidget={smallWidget}
                historicalView={historicalView}
              >
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  style={{ color: palette.error.main }}
                >
                  {CertButtons}
                </Grid>
              </CertBodyGrid>
            ) : null}
          </Fragment>
        );
      }}
    </PatientCertContext.Consumer>
  );
};

const PecosMessageText = styled.p`
  color: ${({ theme }) => theme.palette.error.main};
`;

/**
 * @typedef CertBodyHospiceProps
 *  @prop {*} classes
 *  @prop {function} captureNarrative
 *  @prop {function} copyText
 *  @prop {string} [narrative]
 *  @prop {function} pasteText
 *  @prop {string} textCopy
 *  @prop {boolean} alreadyCertified
 *  @prop {string} certPhysName
 *  @prop {function} formatMessage
 *  @prop {f2fDetails} f2fDetails:
 *  @prop {*} formPhys
 *  @prop {boolean} [historicalView]
 *  @prop {boolean} isDoctor
 *  @prop {boolean} print
 *  @prop {boolean} submitting
 *  @prop {number} selectedCertIndex
 *  @prop {string|number} userId
 *  @prop {*} [updates]
 */
/**
 *
 * @param {CertBodyHospiceProps} props
 */
const CertBodyHospice = ({
  classes,
  captureNarrative,
  copyText,
  narrative,
  pasteText,
  textCopy,
  alreadyCertified,
  pastingofnarratives,
  additionalnarrative,
  certPhysName,
  formatMessage,
  f2fDetails: {
    date: f2fDate,
    link: f2fLink,
    required: f2fRequired,
    provider: f2fProvider,
  },
  formPhys,
  historicalView,
  isDoctor,
  print,
  submitting,
  selectedCertIndex,
  takenBy,
  userId,
  smallWidget,
  updates,
  f2fMissing,
  trailingMessage,
  openVisitDetails,
}) => {
  const disabledNarrative = !isDoctor || alreadyCertified || submitting;
  return (
    <Fragment>
      <CertDescriptionStatement
        classes={classes}
        phys={certPhysName}
        isProvider={
          (!alreadyCertified && userId === f2fProvider?.id) ||
          (alreadyCertified && formPhys.id === f2fProvider?.id)
        }
        providerFirst={f2fProvider?.firstName}
        providerLast={f2fProvider?.lastName}
        providerCred={f2fProvider?.credentials}
        f2fDate={f2fDate}
        f2fLink={f2fLink}
        f2fRequired={f2fRequired}
        smallWidget={smallWidget}
        f2fMissing={f2fMissing}
        additionalnarrative={additionalnarrative}
        openVisitDetails={openVisitDetails}
        historicalView={historicalView}
      />

      {/** narrative box showing logic */}
      {smallWidget && disabledNarrative && !narrative ? null : (
        <Grid item xs={12}>
          <Typography
            style={{ fontSize: getFontSize(smallWidget, historicalView) }}
          >
            <FormattedMessage {...messages.narrative} />
          </Typography>
          <div style={{ position: 'relative' }}>
            <CertNarrativeField
              historicalView={historicalView}
              narrative={narrative}
              smallWidget={smallWidget}
              trailingMessage={trailingMessage}
              inputProps={{
                alreadyCertified,
                pastingofnarratives,
                captureNarrative,
                copyText,
                classes,
                formatMessage,
                isDoctor,
                narrative,
                pasteText,
                print,
                selectedCertIndex,
                submitting,
                takenBy,
                textCopy,
                updates,
              }}
            />
          </div>
        </Grid>
      )}
    </Fragment>
  );
};

export const CertBodyTransfer = () => (
  <CertBodyGrid>
    <FormattedMessage {...messages.docReceived} />
  </CertBodyGrid>
);

const DefaultCertBody = ({
  classes,
  Footer,
  narrative,
  pastingofnarratives,
  alreadyCertified,
  attendingRequired,
  canCertify,
  errorState,
  hospiceFormProps,
  formatMessage,
  formNarrativeId,
  formPhysVerbalDate,
  formType,
  handleCertify,
  isDoctor,
  noNarrative,
  submitting,
  selectedCertIndex,
  takenBy,
  attendingPhysicianId,
  userId,
  smallWidget,
  f2fMissing,
  trailingMessage,
  additionalnarrative,
  openVisitDetails,
  historicalView,
}) => {
  let print;
  if (!isDoctor && !alreadyCertified) {
    print = true;
  }
  const showCertButton = !formNarrativeId && !errorState;
  const showVerbal = !formPhysVerbalDate;

  const { certStartDate, verbalDate } = useContext(CertContext);

  const timelyVerbal =
    moment(verbalDate).diff(moment(certStartDate), 'days') <= 2 ||
    moment(new Date()).diff(moment(certStartDate), 'days') <= 2;
  const lateCert =
    moment(new Date()).diff(moment(certStartDate), 'days') > 2 &&
    !timelyVerbal &&
    !alreadyCertified &&
    !f2fMissing &&
    isDoctor;

  // OP-33276: the Verbal/Sign click held while the late-cert warning is open
  // ({ endpoint, successmsg } of the clicked button). Lives here rather than in
  // CertificationButtons because CertButtons below is re-created every render.
  const [pendingCertify, setPendingCertify] = useState(null);
  useEffect(() => {
    setPendingCertify(null);
  }, [lateCert, certStartDate, verbalDate, formType, selectedCertIndex]);

  const CertButtons = () =>
    showCertButton ? (
      <CertificationButtons
        padTop={!!Footer}
        noNarrative={noNarrative}
        isDoctor={isDoctor}
        canCertify={canCertify}
        submitting={submitting}
        handleCertify={handleCertify}
        showVerbal={showVerbal}
        alreadyCertified={alreadyCertified}
        formType={formType}
        userId={userId}
        attendingPhysicianId={attendingPhysicianId}
        smallWidget={smallWidget}
        lateCert={lateCert}
        pendingCertify={pendingCertify}
        setPendingCertify={setPendingCertify}
      />
    ) : null;
  return (
    <Fragment>
      {alreadyCertified && attendingRequired ? (
        <CertBodyGrid
          typographyProps={{ color: 'error' }}
          smallWidget={smallWidget}
          historicalView={historicalView}
        >
          <FormattedMessage {...messages.awaitAttending} />
        </CertBodyGrid>
      ) : null}
      {formType === ATTENDING_FORM ? (
        <CertBodyAttending
          trailingMessage={trailingMessage}
          smallWidget={smallWidget}
          Footer={Footer}
          CertButtons={<CertButtons />}
          additionalnarrative={additionalnarrative}
          historicalView={historicalView}
        />
      ) : (
        <Fragment>
          <CertBodyHospice
            classes={classes}
            alreadyCertified={alreadyCertified}
            formatMessage={formatMessage}
            isDoctor={isDoctor}
            additionalnarrative={additionalnarrative}
            narrative={narrative}
            pastingofnarratives={pastingofnarratives}
            print={print}
            selectedCertIndex={selectedCertIndex}
            submitting={submitting}
            takenBy={takenBy}
            smallWidget={smallWidget}
            f2fMissing={f2fMissing}
            trailingMessage={trailingMessage}
            openVisitDetails={openVisitDetails}
            {...hospiceFormProps}
          />
          {!formNarrativeId && alreadyCertified ? null : (
            <CertConfirmationStatement
              key={`cert-footer-${formType}-${selectedCertIndex}`}
              CertButtons={<CertButtons />}
              Footer={Footer}
              isDoctor={isDoctor}
              showVerbal={showVerbal}
              smallWidget={smallWidget}
              historicalView={historicalView}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

/**
 * @typedef Props
 * @prop {any} classes
 * @prop {React.ReactElement} Footer
 * @prop {'loader'|'noAttendingRequired' | 'errorState' | 'default' | 'verbalOnly'} body
 * @prop {boolean} transferredAndVerified
 * @prop {{ [k: string ]: string }} errors
 * @prop {boolean} alreadyCertified
 * @prop {boolean} pastingofnarratives
 * @prop {boolean} attendingRequired
 * @prop {boolean} canCertify
 * @prop {string} [narrative]
 * @prop {CertBodyHospiceProps} hospiceFormProps
 * @prop {function} formatMessage
 * @prop {string|number} [formNarrativeId]
 * @prop {string} [formPhysVerbalDate]
 * @prop {'CERTIFYING'|'ATTENDING'} formType
 * @prop {function} handleCertify
 * @prop {boolean} isDoctor
 * @prop {boolean} noNarrative
 * @prop {boolean} submitting
 * @prop {number} selectedCertIndex
 * @prop {boolean} [embedded]

 */
/**
 * @param {Props} props
 */
// TODO
export const CertBody = ({
  classes,
  Footer,
  body,
  transferredAndVerified,
  errors,
  embedded,
  smallWidget,
  f2fMissing,
  additionalnarrative,
  openVisitDetails,
  historicalView,
  ...rest
}) => (
  <Fragment>
    <div style={embedded ? { minHeight: '196px' } : {}}>
      {
        {
          loader: (
            <Grid
              container
              className={classes.loaderGridContainerBody}
              item
              xs={12}
            >
              <Loader contained />
            </Grid>
          ),
          noAttendingPecos: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              <CertBodyGrid smallWidget={smallWidget}>
                <FormattedMessage {...messages.noAttendingPecos} />
              </CertBodyGrid>
            </Grid>
          ),
          noAttendingRequired: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              <CertBodyGrid
                smallWidget={smallWidget}
                historicalView={historicalView}
              >
                <FormattedMessage {...messages.noAttendingRequired} />
              </CertBodyGrid>
            </Grid>
          ),
          errorState: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              {Object.entries(errors).map(([key, error]) =>
                error ? (
                  <CertBodyGrid
                    key={key}
                    className={classes.errorMessage}
                    smallWidget={smallWidget}
                    historicalView={historicalView}
                  >
                    <FormattedMessage {...messages[key]} />
                  </CertBodyGrid>
                ) : null,
              )}
              {/** do not change order */}
              <DefaultCertBody
                classes={classes}
                Footer={Footer}
                smallWidget={smallWidget}
                f2fMissing={f2fMissing}
                {...rest}
                errorState
                isDoctor={false}
                additionalnarrative={additionalnarrative}
                openVisitDetails={openVisitDetails}
                historicalView={historicalView}
              />
            </Grid>
          ),
          default: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              {transferredAndVerified ? (
                <CertBodyTransfer />
              ) : (
                <DefaultCertBody
                  classes={classes}
                  smallWidget={smallWidget}
                  f2fMissing={f2fMissing}
                  {...rest}
                  Footer={Footer}
                  additionalnarrative={additionalnarrative}
                  openVisitDetails={openVisitDetails}
                  historicalView={historicalView}
                />
              )}
            </Grid>
          ),
          verbalOnly: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              <CertBodyGrid
                smallWidget={smallWidget}
                historicalView={historicalView}
              >
                <FormattedMessage {...messages.verbalOnly} />
              </CertBodyGrid>
            </Grid>
          ),
          notPecosApproved: (
            <Grid container className={classes.gridContainerBody} item xs={12}>
              <PecosMessageText
                style={{ fontSize: getFontSize(smallWidget, historicalView) }}
              >
                <strong>
                  <FormattedMessage {...messages.pecosPhysSignatureMsg} />
                </strong>
              </PecosMessageText>
            </Grid>
          ),
        }[body]
      }
    </div>
  </Fragment>
);
